/**
 * Setores que esta portaria valida — e onde a carga do evento acontece.
 *
 * Ao entrar, já começa a baixar setores e ingressos, mostrando a lista crescer
 * em vez de segurar o operador numa tela parada. O botão de atualizar repete a
 * carga quando o produtor mexeu no evento durante a portaria.
 *
 * O que estiver desligado aqui vira a recusa "Não pertence ao setor" na regra —
 * é assim que a mesma base serve várias portarias do mesmo evento. Por isso o
 * cartão do setor desligado diz "recusa aqui" em vez de só ficar apagado: é a
 * consequência, não o estado.
 */

import React, {useCallback, useEffect, useRef, useState} from 'react';
import {ActivityIndicator, Alert, FlatList, Pressable, Switch, Text, View} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useFocusEffect} from '@react-navigation/native';
import {CloudUpload, Lock, Pencil, RotateCw, ScanLine, Search} from 'lucide-react-native';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import {ErroApi} from '../api/cliente';
import {caixaAberto, caixaEmCache, type Caixa} from '../caixa';
import {
  contagensDosSetores,
  definirSetorHabilitado,
  definirTodosSetores,
  listarSetores,
  resumoDoEvento,
  ultimaSincronizacao,
  type ContagemSetor,
  type ResumoEvento,
} from '../dados/repositorio';
import type {Setor} from '../dominio/tipos';
import {ehEventoDeDemonstracao} from '../dominio/demonstracao';
import {carregarSessao} from '../sessao';
import {prepararEvento} from '../sync/sincronizar';
import {DialogoSenha} from '../ui/DialogoSenha';
import {contagem, desdeQuando} from '../ui/datas';
import {txt, useIdioma} from '../ui/idioma';
import {estilos, fontes, useTema} from '../ui/tema';
import type {ParamsPilha} from '../navegacao';

type Props = NativeStackScreenProps<ParamsPilha, 'Setores'>;

const SEM_CONTAGEM: ContagemSetor = {ingressos: 0, nestaMaquina: 0};

export function TelaSetores({navigation, route}: Props) {
  const {eventoId, eventoNome} = route.params;
  const t = useTema();
  useIdioma();

  const [setores, setSetores] = useState<Setor[]>([]);
  const [contagens, setContagens] = useState<Record<number, ContagemSetor>>({});
  const [resumo, setResumo] = useState<ResumoEvento | null>(null);
  const [carregadoEm, setCarregadoEm] = useState<string | undefined>();
  const [caixa, setCaixa] = useState<Caixa | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [etapa, setEtapa] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [liberado, setLiberado] = useState(false);
  const [liberadoPor, setLiberadoPor] = useState<string | undefined>();
  const [liberadoOffline, setLiberadoOffline] = useState(false);
  const [pedindoSenha, setPedindoSenha] = useState(false);
  const jaCarregou = useRef(false);
  /** Liberado só depois de fechar o caixa — ver o bloqueio de volta abaixo. */
  const podeSair = useRef(false);

  /** O fechamento tem tela própria: relatório do aparelho e fila de envio. */
  const irParaFechamento = useCallback(() => {
    navigation.navigate('Fechamento', {eventoId, eventoNome});
  }, [navigation, eventoId, eventoNome]);

  const avisarCaixaAberto = useCallback(() => {
    Alert.alert(
      txt('Ainda aberto'),
      txt('A validação deste aparelho está vinculada a "{posto}". ', {
        posto: caixa?.nome ?? '',
      }) + txt('Para trocar de evento é preciso fechar.'),
      [
        {text: txt('Continuar'), style: 'cancel'},
        {text: txt('Fechar'), style: 'destructive', onPress: irParaFechamento},
      ],
    );
  }, [caixa, irParaFechamento]);

  /**
   * O caixa está amarrado a este evento: sair daqui significaria validar outro
   * evento com a mesma contagem e a mesma fila de envio. A volta — gesto ou
   * tecla do aparelho — só existe fechando o caixa.
   */
  useEffect(() => {
    navigation.setOptions({gestureEnabled: false});

    return navigation.addListener('beforeRemove', evento => {
      // Caixa já fechado (a tela de fechamento limpou) — a saída é legítima.
      if (podeSair.current || caixaEmCache() === null) {
        return;
      }
      evento.preventDefault();
      avisarCaixaAberto();
    });
  }, [navigation, avisarCaixaAberto]);

  const recarregarDoBanco = useCallback(async () => {
    const [lista, numeros, contagensSetor, quando] = await Promise.all([
      listarSetores(eventoId),
      resumoDoEvento(eventoId),
      contagensDosSetores(eventoId),
      ultimaSincronizacao(eventoId),
    ]);
    setSetores(lista);
    setResumo(numeros);
    setContagens(contagensSetor);
    setCarregadoEm(quando);
  }, [eventoId]);

  /**
   * Baixa a carga do evento mostrando a lista se formando durante o processo.
   *
   * `cargaCompleta` só no toque do operador. Na entrada da tela vale o delta,
   * que já traz os nominais — mesma divisão do TMobi, onde a recarga completa
   * mora no botão de atualizar e não no ciclo automático.
   */
  const carregarDoServidor = useCallback(
    async (cargaCompleta = false) => {
      const sessao = await carregarSessao();
      if (!sessao) {
        navigation.replace('Login');
        return;
      }
      // Demonstração não baixa nada: os setores e ingressos já foram semeados
      // no login, e a tela lê do banco como sempre.
      if (ehEventoDeDemonstracao(eventoId)) {
        await recarregarDoBanco();
        return;
      }

      setCarregando(true);
      setErro(null);
      try {
        await prepararEvento(
          eventoId,
          sessao.machineId,
          progresso => {
            setEtapa(progresso.etapa);
            // A cada etapa o banco já tem mais coisa: a tela acompanha.
            recarregarDoBanco();
          },
          cargaCompleta,
        );
      } catch (e) {
        setErro(
          e instanceof ErroApi
            ? e.message
            : txt('Falha de rede. A portaria segue com o que já foi baixado.'),
        );
      } finally {
        setCarregando(false);
        setEtapa('');
        await recarregarDoBanco();
      }
    },
    [eventoId, navigation, recarregarDoBanco],
  );

  useFocusEffect(
    useCallback(() => {
      caixaAberto().then(setCaixa);
      recarregarDoBanco();
    }, [recarregarDoBanco]),
  );

  useEffect(() => {
    // Carga automática ao entrar, uma vez por visita à tela.
    if (!jaCarregou.current) {
      jaCarregou.current = true;
      carregarDoServidor();
    }
  }, [carregarDoServidor]);

  async function alternar(setor: Setor, habilitado: boolean) {
    // Otimista: a portaria não pode esperar o banco para ver o switch mexer.
    setSetores(atual =>
      atual.map(s => (s.remoteId === setor.remoteId ? {...s, habilitado} : s)),
    );
    await definirSetorHabilitado(setor.remoteId, habilitado);
  }

  async function todos(habilitado: boolean) {
    await definirTodosSetores(eventoId, habilitado);
    await recarregarDoBanco();
  }

  const habilitados = setores.filter(s => s.habilitado).length;

  /**
   * Depois que a portaria começa, setor não muda no impulso: mudar aqui muda
   * quem entra. Só destrava com credencial de quem tem acesso, e volta a
   * travar quando o operador retorna para a câmera.
   */
  const travado = Boolean(caixa?.setoresTravados) && !liberado;

  /**
   * A base do evento chegou.
   *
   * Medido pelos lotes, não pelos nominais: evento sem nenhum ingresso ao
   * portador é comum, e antes isso aparecia como "Nenhum ingresso baixado"
   * mesmo com a base inteira no aparelho. Sem lote é que não se valida nada.
   */
  const baixou = Boolean(resumo && (resumo.lotes > 0 || resumo.ingressos > 0));

  /** Cartão de estado no topo: carga em andamento, trava, ou base pronta. */
  function estado() {
    if (carregando) {
      return (
        <View
          style={[
            estilos.cartao,
            {backgroundColor: t.superficie, borderColor: t.borda, flexDirection: 'row', alignItems: 'center', gap: 12},
          ]}>
          <ActivityIndicator color={t.primaria} />
          <Text style={{color: t.textoMedio, fontSize: 15, fontFamily: fontes.medio, flex: 1}}>
            {etapa || txt('Carregando...')}
          </Text>
        </View>
      );
    }

    if (caixa?.setoresTravados) {
      return (
        <View
          style={[
            estilos.cartao,
            {
              backgroundColor: t.superficie,
              borderColor: t.borda,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              padding: 16,
            },
          ]}>
          <View
            style={{
              width: 38,
              height: 38,
              borderRadius: 11,
              backgroundColor: t.superficieAlta,
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Lock size={19} color={travado ? t.aviso : t.primaria} strokeWidth={2.2} />
          </View>

          <View style={{flex: 1, gap: 2}}>
            <Text style={{color: t.texto, fontSize: 15, fontFamily: fontes.negrito}}>
              {travado ? txt('Setores travados') : txt('Edição liberada')}
            </Text>
            <Text style={{color: t.textoFraco, fontSize: 13, fontFamily: fontes.regular, lineHeight: 18}}>
              {travado
                ? txt('A portaria já começou. Toque para liberar.')
                : liberadoPor
                  ? txt('Liberado por {quem}', {quem: liberadoPor}) +
                    (liberadoOffline ? txt(' (sem rede, pela lista do aparelho)') : '')
                  : txt('Liberado')}
            </Text>
          </View>

          {travado ? (
            <Pressable
              onPress={() => setPedindoSenha(true)}
              hitSlop={8}
              style={{
                height: 40,
                paddingHorizontal: 14,
                borderRadius: 11,
                borderWidth: 1,
                borderColor: t.primaria,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 7,
              }}>
              <Pencil size={16} color={t.primaria} strokeWidth={2.2} />
              <Text style={{color: t.primaria, fontSize: 14, fontFamily: fontes.negrito}}>
                {txt('Liberar')}
              </Text>
            </Pressable>
          ) : null}
        </View>
      );
    }

    return (
      <View
        style={[
          estilos.cartao,
          {
            backgroundColor: t.superficie,
            borderColor: t.borda,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 12,
            padding: 16,
          },
        ]}>
        <View
          style={{
            width: 38,
            height: 38,
            borderRadius: 11,
            backgroundColor: t.superficieAlta,
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <CloudUpload size={19} color={t.primaria} strokeWidth={2.2} />
        </View>
        <View style={{flex: 1, gap: 2}}>
          <Text style={{color: t.texto, fontSize: 15, fontFamily: fontes.negrito}}>
            {baixou ? txt('Carregamento concluído') : txt('Nenhum ingresso baixado')}
          </Text>
          <Text
            style={{
              color: t.textoFraco,
              fontSize: 13,
              fontFamily: fontes.regular,
              fontVariant: ['tabular-nums'],
            }}>
            {resumo && baixou
              ? [
                  // A base inteira, não só a parte nominal: o ingresso comum
                  // entra pela faixa de lote, e sem lote não se valida nada.
                  contagem(resumo.lotes, txt('lote'), txt('lotes')),
                  resumo.ingressos > 0
                    ? contagem(resumo.ingressos, txt('nominal'), txt('nominais'))
                    : null,
                  resumo.cancelados > 0
                    ? contagem(resumo.cancelados, txt('cancelado'), txt('cancelados'))
                    : null,
                  desdeQuando(carregadoEm),
                ]
                  .filter(Boolean)
                  .join(' · ')
              : txt('Toque em atualizar com sinal.')}
          </Text>
        </View>
      </View>
    );
  }

  return (
    <SafeAreaView style={[estilos.tela, {backgroundColor: t.fundo}]} edges={['top', 'bottom']}>
      <View style={[estilos.cabecalho, {borderBottomColor: t.superficie}]}>
        <View style={{flex: 1}}>
          <Text style={[estilos.tituloCabecalho, {color: t.texto}]}>{txt('Setores')}</Text>
          <Text style={[estilos.legendaCabecalho, {color: t.textoFraco}]} numberOfLines={1}>
            {[eventoNome, caixa?.nome].filter(Boolean).join(' · ')}
          </Text>
        </View>

        <Pressable
          // Com argumento explícito: `onPress={carregarDoServidor}` passaria o
          // evento do toque como `cargaCompleta`, e um objeto é sempre truthy.
          // Daria certo por acidente, o que é pior que dar errado.
          onPress={() => carregarDoServidor(true)}
          disabled={carregando}
          hitSlop={6}
          accessibilityLabel={txt('Atualizar setores e recarregar ingressos')}
          style={[estilos.botaoIcone, {borderColor: t.borda, opacity: carregando ? 0.4 : 1}]}>
          <RotateCw size={20} color={t.primaria} strokeWidth={2.2} />
        </Pressable>
      </View>

      <FlatList
        data={setores}
        keyExtractor={s => String(s.remoteId)}
        contentContainerStyle={{padding: 20, gap: 10}}
        ListHeaderComponent={
          <View style={{gap: 12, marginBottom: 4}}>
            {estado()}

            {erro ? (
              <Text style={[estilos.erro, {color: t.erro, fontSize: 14}]}>{erro}</Text>
            ) : null}

            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}>
              <Text style={{color: t.textoFraco, fontSize: 13, fontFamily: fontes.regular}}>
                <Text style={[estilos.numero, {color: t.texto, fontSize: 13}]}>
                  {habilitados} de {setores.length}
                </Text>{' '}
                {setores.length === 1 ? txt('setor Ativo  ') : txt('setores Ativos  ')}
              </Text>

              <View style={{flexDirection: 'row', gap: 8}}>
                {[
                  {texto: txt('Todos'), acao: () => todos(true)},
                  {texto: txt('Nenhum'), acao: () => todos(false)},
                ].map(botao => (
                  <Pressable
                    key={botao.texto}
                    onPress={botao.acao}
                    disabled={travado}
                    style={{
                      height: 36,
                      paddingHorizontal: 12,
                      borderRadius: 10,
                      borderWidth: 1,
                      borderColor: t.borda,
                      justifyContent: 'center',
                      opacity: travado ? 0.4 : 1,
                    }}>
                    <Text style={{color: t.textoFraco, fontSize: 13, fontFamily: fontes.negrito}}>
                      {botao.texto}
                    </Text>
                  </Pressable>
                ))}
              </View>
            </View>
          </View>
        }
        ListEmptyComponent={
          carregando ? undefined : (
            <Text style={[estilos.subtitulo, {color: t.textoFraco}]}>
              {txt('Nenhum setor baixado para este evento. Toque em atualizar com sinal.')}
            </Text>
          )
        }
        renderItem={({item}) => {
          const numeros = contagens[item.remoteId] ?? SEM_CONTAGEM;
          const ligado = item.habilitado;

          return (
            <View
              style={{
                backgroundColor: ligado ? t.superficie : t.fundo,
                borderWidth: 1,
                borderColor: ligado ? t.primaria : t.borda,
                borderRadius: 16,
                padding: 16,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 14,
              }}>
              <View style={{flex: 1, gap: 3}}>
                <Text
                  style={{
                    color: ligado ? t.texto : t.textoApagado,
                    fontSize: 17,
                    fontFamily: fontes.negrito,
                  }}>
                  {item.nome || txt('Setor {numero}', {numero: item.remoteId})}
                </Text>
                <Text
                  style={{
                    color: ligado ? t.textoFraco : t.textoApagado,
                    fontSize: 13,
                    fontFamily: fontes.regular,
                    fontVariant: ['tabular-nums'],
                  }}>
                  {/* O que ESTE aparelho liberou por aqui. O número de todas
                      as portarias não existe por setor: validação de outro
                      aparelho desce sem setor (ver salvarValidadosDoServidor). */}
                  {contagem(numeros.nestaMaquina, txt('validado'), txt('validados')) +
                    (ligado ? '' : txt(' · recusa aqui'))}
                </Text>
              </View>

              <Switch
                value={ligado}
                onValueChange={v => alternar(item, v)}
                disabled={travado}
                trackColor={{true: t.primaria, false: t.superficieAlta}}
                thumbColor={t.texto}
              />
            </View>
          );
        }}
      />

      <View
        style={{
          padding: 20,
          gap: 10,
          borderTopWidth: 1,
          borderTopColor: t.superficie,
        }}>
        <View style={{flexDirection: 'row', gap: 10}}>
          <Pressable
            onPress={() => navigation.navigate('Ingressos', {eventoId, eventoNome})}
            style={[estilos.botaoSecundario, {flex: 1, height: 48, borderColor: t.borda}]}>
            <Search size={17} color={t.textoFraco} strokeWidth={2.2} />
            <Text style={[estilos.textoBotaoSecundario, {color: t.textoFraco, fontSize: 15}]}>
              {txt('Ingressos')}
            </Text>
          </Pressable>

          <Pressable
            onPress={irParaFechamento}
            style={[estilos.botaoSecundario, {flex: 1, height: 48, borderColor: t.borda}]}>
            <Text style={[estilos.textoBotaoSecundario, {color: t.textoFraco, fontSize: 15}]}>
              {txt('Fechar')}
            </Text>
          </Pressable>
        </View>

        <Pressable
          onPress={() => {
            // Voltando para a câmera, a liberação acaba: o próximo que quiser
            // mexer nos setores precisa chamar quem tem acesso de novo.
            setLiberado(false);
            setLiberadoPor(undefined);
            navigation.navigate('Validador', {eventoId, eventoNome});
          }}
          disabled={habilitados === 0}
          style={[
            estilos.botao,
            {height: 62, backgroundColor: habilitados === 0 ? t.superficieAlta : t.primaria},
          ]}>
          <ScanLine
            size={22}
            color={habilitados === 0 ? t.textoApagado : t.tintaPrimaria}
            strokeWidth={2.6}
          />
          <Text
            style={[
              estilos.textoBotao,
              {fontSize: 19, color: habilitados === 0 ? t.textoApagado : t.tintaPrimaria},
            ]}>
            {habilitados === 0 ? txt('Escolha ao menos um setor') : txt('Validar')}
          </Text>
        </Pressable>
      </View>

      <DialogoSenha
        visivel={pedindoSenha}
        titulo={txt('Liberar edição de setores')}
        descricao={
          txt('A portaria já começou neste aparelho{posto}. ', {
            posto: caixa?.nome ? ` (${caixa.nome})` : '',
          }) + txt('Alterar setores muda quem entra: peça a quem tem acesso.')
        }
        aoFechar={() => setPedindoSenha(false)}
        aoLiberar={(nome, offline) => {
          setPedindoSenha(false);
          setLiberado(true);
          setLiberadoPor(nome);
          setLiberadoOffline(Boolean(offline));
        }}
      />
    </SafeAreaView>
  );
}
